import React, { useEffect, useState, useContext } from "react";

import LoadingContext from "../../context/Loading/Loading";
import UserContext from "../../context/User/User";

import { ORGANIZATION_PERMISSION_SLUGS } from "@blocktickets/shared-utils";
import { checkPermission } from "../../utilities/helpers";

import { useModal } from "../../providers/ModalProvider/ModalProvider";

import AuthService from "../../utilities/services/auth.service";

import {
  getPromoCodes,
  updatePromoCodeActive,
  deletePromoCode,
} from "../../utilities/api";

import Card from "react-bootstrap/Card";
import Stack from "react-bootstrap/Stack";
import Button from "react-bootstrap/Button";

import { EmptyContainer } from "../EmptyContainer";
import { PageLoadingContainer } from "../PageLoadingContainer";
import { NoPermissionsContainer } from "../NoPermissionsContainer";
import { AddPromoCode } from "./AddPromoCode";
import { PromoCode } from "./PromoCode";
import { Delete } from "../ModalContent";

import "./promoCodesWrapper.scss";

export default function PromoCodesWrapper() {
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext);
  const { orgPermissions } = useContext(UserContext);
  const { getPermissions } = AuthService;

  const orgPermissionsReady = orgPermissions?.length > 0;
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.MANAGE_PROMO_CODES,
    );

  const { openModal, closeModal } = useModal();

  const organization = AuthService.getOrg()[0];

  const [promoCodes, setPromoCodes] = useState();

  const priceOpts = [
    // {
    //   id: 1,
    //   value: "custom_price",
    //   label: "Custom price",
    // },
    {
      id: 2,
      value: "decrease_by_price",
      label: "$ Decrease",
    },
    {
      id: 3,
      value: "decrease_by_percent",
      label: "% Decrease",
    },
  ];

  const applyToOpts = [
    {
      id: 1,
      value: "total_price",
      label: "Total price",
    },
    {
      id: 2,
      value: "individual_tickets",
      label: "Per ticket",
    },
  ];

  const limitOpts = [
    {
      id: 1,
      value: "unlimited",
      label: "Unlimited",
    },
    {
      id: 2,
      value: "fixed_amount",
      label: "Fixed amount (times)",
    },
  ];

  useEffect(() => {
    if (!orgPermissionsReady) return;
    if (!hasPermission) {
      hideLoading();
      return;
    }
    showLoading();
    loadPromoCodes()
      .catch((err) => console.error(err))
      .finally(() => hideLoading());
  }, [orgPermissionsReady, hasPermission, organization?.uuid]);

  const loadPromoCodes = () => {
    return getPromoCodes(organization.uuid)
      .then((res) => {
        setPromoCodes(res.data);
      })
      .catch((err) => {
        console.error("Failed to load promo codes:", err);
        throw err; // rethrow so .catch or await knows it failed
      });
  };

  const openAddPromoCode = (_, id) => {
    const promoCode = promoCodes[id];
    openModal(
      AddPromoCode,
      {
        organization,
        priceOpts,
        applyToOpts,
        promoCode,
        limitOpts,
        loadPromoCodes,
      },
      { className: "vjx-modal-2xl", contentClassName: "", scrollable: true },
    );
  };

  const removePromoCode = (id) => {
    openModal(
      Delete,
      {
        entity: "promo code",
        id,
        handleDelete: deleteCode,
        onClose: closeModal,
      },
      { className: "", contentClassName: "", scrollable: false },
    );
  };

  const deleteCode = (id) => {
    return new Promise((resolve, reject) => {
      deletePromoCode(id)
        .then((res) => {
          delete promoCodes[id];
          resolve(res);
        })
        .catch((err) => {
          console.error(err);
          reject(err);
        });
    });
  };

  const handleIsActive = (_, id, isActive) => {
    setPromoCodes((prevState) => ({
      ...prevState,
      [id]: {
        ...prevState[id],
        isActive,
      },
    }));
    // update isActive state in db
    updatePromoCodeActive({
      data: {
        id,
        isActive,
      },
    });
  };

  const confirmRemoval = (id) => {
    removePromoCode(id);
  };

  if (!orgPermissionsReady) {
    return <PageLoadingContainer style="without-sidebar" />;
  }

  if (!hasPermission) {
    return (
      <div className="spacer-sm">
        <NoPermissionsContainer />
      </div>
    );
  }

  if (isLoading) {
    return <PageLoadingContainer style="without-sidebar" />;
  }

  return (
    <section className="spacer-sm" data-testid="promo-codes-wrapper-content">
      <header className="section-heading--flex section-header-sm">
        <div className="section-heading">
          <h1>Promo Codes</h1>
          <p className="section-header-desc">Add promo codes to events.</p>
        </div>
        <Button size="lg" onClick={openAddPromoCode}>
          Add promo code
        </Button>
      </header>
      {promoCodes && Object.keys(promoCodes)?.length > 0 ? (
        <Card body className="card--md card--light">
          <Stack as="ul" gap={4}>
            {Object.values(promoCodes)?.map((promoCode, idx) => (
              <PromoCode
                key={idx}
                promoCode={promoCode}
                priceOpts={priceOpts}
                applyToOpts={applyToOpts}
                limitOpts={limitOpts}
                handleIsActive={handleIsActive}
                openAddPromoCode={openAddPromoCode}
                confirmRemoval={confirmRemoval}
              />
            ))}
          </Stack>
        </Card>
      ) : (
        <Card body>
          <EmptyContainer style="center lg discount">
            <p>No promo codes added</p>
          </EmptyContainer>
        </Card>
      )}
    </section>
  );
}
